import { jsPDF } from "jspdf";
import { UOL_LOGO_BASE64 } from "./logoBase64";

// Pattern dimensions from UOL Paper catalog
const PATTERN_W = 600;
const PATTERN_H = 290;

function fmtEur(n) {
  return n.toFixed(2).replace(".", ",") + " \u20AC";
}

function fmtDate() {
  const d = new Date();
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
}

// Compress image data URL to JPEG with maxDim for PDF embedding
function compressImage(dataUrl, maxDim = 800) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      resolve({ dataUrl: c.toDataURL("image/jpeg", 0.75), w, h });
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

/**
 * Generate a professional PDF quote for UOL Paper simulation
 */
export async function generateQuotePDF({
  wallpaper,
  collection,
  resultImage,
  wallWidthCm,
  wallHeightCm,
  method,
  user,
}) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();  // 210
  const ph = doc.internal.pageSize.getHeight(); // 297
  const mx = 20; // margin x
  const contentW = pw - mx * 2; // 170
  let y = 0;

  // Colors
  const dark = [28, 25, 23];       // stone-950
  const mid = [120, 113, 108];     // stone-500
  const light = [168, 162, 158];   // stone-400
  const accent = [217, 119, 6];    // amber-600
  const bg = [250, 250, 249];      // stone-50

  // ─── HEADER BAR ───
  doc.setFillColor(...dark);
  doc.rect(0, 0, pw, 32, "F");

  // Logo image in header
  try {
    doc.addImage(UOL_LOGO_BASE64, "PNG", mx, 4, 24, 24);
  } catch { /* fallback: no logo */ }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(180, 180, 180);
  doc.text("ARTE MODULARE", mx + 28, 15);
  doc.text("SIMULATORE CARTA DA PARATI", mx + 28, 22);

  // Date right-aligned
  doc.setFontSize(8);
  doc.setTextColor(180, 180, 180);
  doc.text(fmtDate(), pw - mx, 17, { align: "right" });
  doc.text("PREVENTIVO", pw - mx, 25, { align: "right" });

  y = 42;

  // ─── WALLPAPER INFO ───
  doc.setFontSize(8);
  doc.setTextColor(...light);
  doc.setFont("helvetica", "normal");
  doc.text("SOGGETTO", mx, y);
  y += 5;

  doc.setFontSize(16);
  doc.setTextColor(...dark);
  doc.text(wallpaper?.name || "—", mx, y);

  // Collection badge
  const collName = collection?.name || "";
  const collType = collection?.collection_type === "artist" ? "ARTIST STRIPES" : "BASELINE";
  doc.setFontSize(9);
  doc.setTextColor(...mid);
  doc.text(`${collName}  |  ${collType}`, mx, y + 7);
  y += 18;

  // ─── SIMULATION IMAGE ───
  if (resultImage) {
    const compressed = await compressImage(resultImage, 1000);
    if (compressed) {
      const imgMaxW = contentW;
      const imgMaxH = 90;
      const ratio = compressed.w / compressed.h;
      let imgW = imgMaxW;
      let imgH = imgW / ratio;
      if (imgH > imgMaxH) {
        imgH = imgMaxH;
        imgW = imgH * ratio;
      }
      const imgX = mx + (contentW - imgW) / 2;

      // Light background behind image
      doc.setFillColor(...bg);
      doc.rect(mx, y, contentW, imgH + 4, "F");
      doc.addImage(compressed.dataUrl, "JPEG", imgX, y + 2, imgW, imgH);
      y += imgH + 8;

      doc.setFontSize(7);
      doc.setTextColor(...light);
      doc.text(`Simulazione ${method === "ai" ? "AI" : "Manuale"}`, mx, y);
      y += 8;
    }
  }

  // ─── DIMENSIONS TABLE ───
  doc.setFillColor(...dark);
  doc.rect(mx, y, contentW, 8, "F");
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.text("DETTAGLI PREVENTIVO", mx + 4, y + 5.5);
  y += 8;

  const wCm = wallWidthCm || 300;
  const hCm = wallHeightCm || 270;
  const areaSqm = (wCm / 100) * (hCm / 100);
  const panelsX = Math.ceil(wCm / PATTERN_W);
  const panelsY = Math.ceil(hCm / PATTERN_H);
  const totalPanels = panelsX * panelsY;
  const pricePerSqm = collection?.price_per_sqm || 122;
  const totalPrice = areaSqm * pricePerSqm;

  const rows = [
    ["Larghezza parete", `${wCm} cm`],
    ["Altezza parete", `${hCm} cm`],
    ["Dimensione pattern", `${PATTERN_W} × ${PATTERN_H} cm`],
    ["Teli necessari", `${totalPanels} (${panelsX} × ${panelsY})`],
    ["Superficie", `${areaSqm.toFixed(2)} mq`],
    ["Prezzo al mq", fmtEur(pricePerSqm)],
  ];

  doc.setFont("helvetica", "normal");
  rows.forEach((row, i) => {
    const rowY = y + i * 9;
    // Alternating row bg
    if (i % 2 === 0) {
      doc.setFillColor(248, 248, 248);
      doc.rect(mx, rowY, contentW, 9, "F");
    }
    doc.setFontSize(9);
    doc.setTextColor(...mid);
    doc.text(row[0], mx + 4, rowY + 6);
    doc.setTextColor(...dark);
    doc.text(row[1], pw - mx - 4, rowY + 6, { align: "right" });
  });

  y += rows.length * 9;

  // Total row
  doc.setFillColor(...dark);
  doc.rect(mx, y, contentW, 12, "F");
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("TOTALE (esclusa posa)", mx + 4, y + 8);
  doc.setFontSize(13);
  doc.text(fmtEur(totalPrice), pw - mx - 4, y + 8.5, { align: "right" });
  y += 18;

  // ─── USER INFO (if available) ───
  if (user?.name || user?.email) {
    doc.setFontSize(7);
    doc.setTextColor(...light);
    doc.text("GENERATO PER", mx, y);
    y += 4;
    doc.setFontSize(9);
    doc.setTextColor(...dark);
    doc.setFont("helvetica", "normal");
    const userLine = [user.name, user.company_name].filter(Boolean).join(" — ");
    if (userLine) {
      doc.text(userLine, mx, y);
      y += 5;
    }
    if (user.email) {
      doc.setTextColor(...mid);
      doc.text(user.email, mx, y);
      y += 5;
    }
    y += 5;
  }

  // ─── FOOTER ───
  const footerY = ph - 22;

  // Separator line
  doc.setDrawColor(...light);
  doc.setLineWidth(0.2);
  doc.line(mx, footerY, pw - mx, footerY);

  // Logo in footer
  try {
    doc.addImage(UOL_LOGO_BASE64, "PNG", mx, footerY + 2, 12, 12);
  } catch { /* fallback */ }

  doc.setFontSize(7);
  doc.setTextColor(...light);
  doc.setFont("helvetica", "normal");
  doc.text("UOL  |  Arte Modulare  |  uolpaper.art", mx + 15, footerY + 6);
  doc.text(
    "Preventivo indicativo. I prezzi non includono il costo di posa in opera. Contattaci per un preventivo definitivo.",
    mx + 15, footerY + 11
  );
  doc.text("Preventivo generato automaticamente", pw - mx, footerY + 6, { align: "right" });

  // ─── ACCENT LINE at bottom ───
  doc.setFillColor(...accent);
  doc.rect(0, ph - 3, pw, 3, "F");

  // Save
  const fileName = `preventivo-${(wallpaper?.name || "uol-paper").toLowerCase().replace(/\s+/g, "-")}-${wCm}x${hCm}.pdf`;
  doc.save(fileName);
  return fileName;
}
